/**
 * Static clinical and editorial copy for the Health Analytics report.
 *
 * Only text that carries no measurement lives here — definitions, symptom lists and the qualitative
 * explanations behind a pattern. Every number, and every claim that depends on a number, is templated
 * from the API response instead.
 */

export const SYNDROME_DEFINITION = {
  eyebrow: 'Definition',
  title: 'What metabolic syndrome means',
  body: 'Metabolic syndrome is a group of conditions that together increase your risk of cardiovascular '
    + 'disease, type 2 diabetes, stroke, and conditions related to plaque buildup in artery walls '
    + '(atherosclerosis) and organ damage.',
};

export const BUCKET_META = [
  { label: 'No abnormalities', caption: 'healthy baseline', accent: 'green' },
  { label: 'One abnormality', caption: 'early warning', accent: 'blue' },
  { label: 'Two abnormalities', caption: 'pre-syndrome zone', accent: 'amber' },
  { label: 'Three or more', caption: 'meets clinical criteria', accent: 'rose' },
];

export const AXIS_META = [
  { tick: '0', caption: 'None' },
  { tick: '1', caption: 'Early warning' },
  { tick: '2', caption: 'Pre-syndrome' },
  { tick: '3+', caption: 'Syndrome' },
];

export const GENDER_EPIDEMIOLOGY = 'This mirrors well-established epidemiology: men accumulate visceral fat '
  + 'and dyslipidemia earlier, while women retain a relative cardiometabolic advantage until after menopause.';

export const INFLECTION_CONTEXT = "This band is typically an org's senior IC / early-management layer — "
  + 'high institutional value, high replacement cost.';

export const SYMPTOMS = {
  vitamin_d: 'Fatigue, bone & muscle pain, low mood, weakened immunity',
  vitamin_b12: 'Fatigue, brain fog, poor concentration, memory lapses, nerve tingling',
  anaemia: 'Breathlessness, dizziness, palpitations, reduced physical stamina',
  thyroid: 'Fatigue or anxiety, weight change, cold/heat intolerance, mood shifts',
};

export const SHARED_CAUSE = {
  eyebrow: 'Likely shared cause',
  body: 'This pattern is consistent with low sun exposure and diet in indoor, desk-based work — a single '
    + 'lifestyle driver showing up on two panels, not two independent problems.',
};

export const PRODUCTIVITY_READ_THROUGH = [
  {
    lead: 'Fatigue is the common thread',
    rest: ' across all four conditions — it compounds rather than adds for employees with multiple abnormalities.',
  },
  {
    rest: 'B12 deficiency directly affects concentration and cognitive sharpness — core to desk-based, '
      + 'knowledge work output.',
  },
  {
    rest: 'Anaemia limits physical stamina — a safety and performance concern in physically demanding roles.',
  },
  {
    lead: 'Presenteeism risk:',
    rest: ' employees with 2+ abnormalities are physically present but functionally under-performing — '
      + 'harder to detect than absenteeism, often costlier.',
  },
];

export const AGE_SPREAD_NOTE = 'Unlike the metabolic picture, deficiency burden does not accumulate with age. '
  + 'Targeting by age band would not meaningfully change who gets reached.';

/** Shown instead of a percentage when a cell's denominator is too small to report responsibly. */
export const MIN_COHORT = 6;
export const SMALL_SAMPLE_NOTE = `minimum ${MIN_COHORT} employees required to show data`;
